// Pure client-side alignment checker.
// Compares the achievements, awards and quantified metrics that appear in
// the tailored CV against the motivational letter body so we can warn the
// user before they download/send when the two documents don't tell the
// same story. Zero side effects — safe to call on every render.

export type AlignmentSeverity = "warning" | "error";

export type AlignmentIssue = {
  severity: AlignmentSeverity;
  kind:
    | "missing_achievement"
    | "missing_award"
    | "missing_metric"
    | "role_mismatch"
    | "no_metrics";
  message: string;
  detail?: string;
};

const STOP = new Set([
  "the","and","for","with","from","that","this","have","has","had","was","were","are","been","being",
  "our","their","its","of","in","on","to","by","at","as","an","a","or","but","if","so","is","be","it",
]);

function norm(s: unknown): string {
  return String(s ?? "")
    .toLowerCase()
    .replace(/[\u2018\u2019\u201C\u201D]/g, "'")
    .replace(/[^\p{L}\p{N}%.\s]/gu, " ")
    .replace(/\s+/g, " ")
    .trim();
}

function keywords(text: string, min = 4): string[] {
  return norm(text)
    .split(/\s+/)
    .filter((w) => w.length >= min && !STOP.has(w));
}

/**
 * Extract quantified tokens (numbers, percentages, R-values, "x2", "40+")
 * so we can check the letter kept the metrics the CV promises.
 */
export function extractMetrics(text: string): string[] {
  const out = new Set<string>();
  const src = String(text || "");
  const patterns: RegExp[] = [
    /\bR\s?\d[\d\s.,]*\b/gi,         // R-values
    /\b\d+(?:[.,]\d+)?\s?%/g,        // percentages
    /\b\d{2,}(?:[.,]\d+)?\s?(?:hours?|days?|weeks?|months?|years?|clients?|files?|matters?|staff|people)\b/gi,
    /\b\d+\+/g,                      // "40+"
    /\bx\s?\d+\b/gi,                 // "x2"
  ];
  for (const re of patterns) {
    for (const m of src.matchAll(re)) out.add(m[0].trim());
  }
  return Array.from(out);
}

/**
 * A given achievement/award is "reflected" in the letter when a strong
 * majority of its content words appear in the letter body.
 */
function isReflected(source: string, letterWords: Set<string>): boolean {
  const words = keywords(source);
  if (!words.length) return true;
  const hits = words.filter((w) => letterWords.has(w)).length;
  // Require ~50% of content words to appear (or 2 words for short items).
  const threshold = Math.max(2, Math.ceil(words.length * 0.5));
  return hits >= threshold;
}

export type AlignmentInputs = {
  achievements?: string[];
  awards?: Array<{ title?: string; issuer?: string; year?: string; detail?: string } | string>;
  cvMetricsSource?: string; // typically the CV summary + bullets joined
  extraKeywords?: string[]; // e.g. quantified wins the user typed in
  targetVacancy?: string;
  letterText: string;
};

export function checkCvLetterAlignment(input: AlignmentInputs): AlignmentIssue[] {
  const issues: AlignmentIssue[] = [];
  const letterNorm = norm(input.letterText);
  if (!letterNorm) return issues;
  const letterWords = new Set(keywords(input.letterText, 3));

  // 1. Achievements — every listed achievement should be echoed in the letter.
  const missingAch: string[] = [];
  for (const a of input.achievements || []) {
    if (!a) continue;
    if (!isReflected(a, letterWords)) missingAch.push(a);
  }
  if (missingAch.length) {
    issues.push({
      severity: "warning",
      kind: "missing_achievement",
      message: `${missingAch.length} CV achievement${missingAch.length > 1 ? "s are" : " is"} not reflected in the letter.`,
      detail: missingAch.slice(0, 3).join(" · "),
    });
  }

  // 2. Awards — recruiters expect the letter to name the strongest awards.
  const missingAwards: string[] = [];
  for (const raw of input.awards || []) {
    const title = typeof raw === "string" ? raw : raw?.title;
    if (!title) continue;
    if (!isReflected(title, letterWords)) missingAwards.push(String(title));
  }
  if (missingAwards.length) {
    issues.push({
      severity: "warning",
      kind: "missing_award",
      message: `${missingAwards.length} award${missingAwards.length > 1 ? "s are" : " is"} listed on the CV but not mentioned in the letter.`,
      detail: missingAwards.slice(0, 3).join(" · "),
    });
  }

  // 3. Metrics — every quantified metric from the CV should appear in the letter.
  const cvMetrics = new Set<string>();
  for (const m of extractMetrics(input.cvMetricsSource || "")) cvMetrics.add(m);
  for (const k of input.extraKeywords || []) {
    for (const m of extractMetrics(k)) cvMetrics.add(m);
  }
  const letterMetrics = new Set(extractMetrics(input.letterText).map((m) => m.toLowerCase()));
  const missingMetrics = Array.from(cvMetrics).filter(
    (m) => !letterMetrics.has(m.toLowerCase()),
  );
  if (missingMetrics.length) {
    issues.push({
      severity: "warning",
      kind: "missing_metric",
      message: `${missingMetrics.length} quantified result${missingMetrics.length > 1 ? "s" : ""} from the CV ${missingMetrics.length > 1 ? "are" : "is"} missing from the letter.`,
      detail: missingMetrics.slice(0, 5).join(", "),
    });
  } else if (!cvMetrics.size && !letterMetrics.size) {
    issues.push({
      severity: "warning",
      kind: "no_metrics",
      message: "Neither the CV nor the letter contains a quantified result — add at least one metric to stand out.",
    });
  }

  // 4. Role/vacancy alignment.
  if (input.targetVacancy) {
    const vac = norm(input.targetVacancy);
    // check core content words of the vacancy title appear in the letter
    if (!isReflected(vac, letterWords)) {
      issues.push({
        severity: "error",
        kind: "role_mismatch",
        message: `The letter does not clearly reference the target vacancy "${input.targetVacancy}".`,
        detail: "Regenerate so the opening paragraph names this role.",
      });
    }
  }

  return issues;
}
